export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

export interface LoggerConfig {
  level: LogLevel
  enableConsole: boolean
  enableUserSpecific: boolean
  userId?: string
  environment: 'development' | 'production'
}

export class Logger {
  private config: LoggerConfig

  constructor(config: LoggerConfig) {
    this.config = config
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.level && this.config.enableConsole
  }

  private formatMessage(level: string, message: string, data?: any): string {
    const timestamp = new Date().toISOString()
    const userPrefix = this.config.enableUserSpecific && this.config.userId 
      ? `[User:${this.config.userId}]` 
      : ''
    
    return `${timestamp} [${level}]${userPrefix} ${message}${data ? ` ${JSON.stringify(data)}` : ''}`
  }

  debug(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(this.formatMessage('DEBUG', message, data))
    }
  }

  info(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(this.formatMessage('INFO', message, data))
    }
  }

  warn(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage('WARN', message, data))
    }
  }

  error(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage('ERROR', message, data))
    }
  }

  updateConfig(newConfig: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...newConfig }
  }
}

// Environment-based configuration
const getDefaultLoggerConfig = (): LoggerConfig => {
  const isDevelopment = import.meta.env.MODE === 'development'
  
  return {
    level: isDevelopment ? LogLevel.DEBUG : LogLevel.WARN,
    enableConsole: true,
    enableUserSpecific: isDevelopment,
    environment: isDevelopment ? 'development' : 'production'
  }
}

// Global logger instance
export const logger = new Logger(getDefaultLoggerConfig())

// Logger factory for creating user-specific loggers
export const createUserLogger = (userId: string): Logger => {
  const config = getDefaultLoggerConfig()
  config.userId = userId
  config.enableUserSpecific = true
  return new Logger(config)
}